/**
 * Command Bus - Pub/Sub Event System for Commands
 * Feature: 051-keybindings
 *
 * Provides event bus for command execution using React Context.
 * Components subscribe to commands, keyboard handler emits commands.
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { CommandId } from './commandDefinitions';
import logger from '../logger';

type CommandHandler = () => void | Promise<void>;

/**
 * Simple event bus for commands
 * Implements pub/sub pattern
 */
class CommandBus {
  private handlers = new Map<CommandId, Set<CommandHandler>>();

  /**
   * Subscribe to a command
   * Returns unsubscribe function
   */
  subscribe(commandId: CommandId, handler: CommandHandler): () => void {
    if (!this.handlers.has(commandId)) {
      this.handlers.set(commandId, new Set());
    }

    const handlers = this.handlers.get(commandId);
    if (handlers) {
      handlers.add(handler);
    }

    logger.debug('CommandBus', 'Subscribed to command', { commandId });

    // Return unsubscribe function
    return () => {
      this.handlers.get(commandId)?.delete(handler);
      logger.debug('CommandBus', 'Unsubscribed from command', { commandId });
    };
  }

  /**
   * Emit a command (called by keyboard handler)
   * Executes all registered handlers
   */
  async emit(commandId: CommandId): Promise<void> {
    const handlers = this.handlers.get(commandId);

    if (!handlers || handlers.size === 0) {
      logger.warn('CommandBus', 'Command emitted but no handlers registered', { commandId });
      return;
    }

    logger.info('CommandBus', 'Executing command', {
      commandId,
      handlerCount: handlers.size
    });

    // Execute all handlers (typically just one per component)
    try {
      await Promise.all(Array.from(handlers).map(h => h()));
    } catch (error) {
      logger.error('CommandBus', 'Command handler failed', { commandId, error });
      throw error;
    }
  }

  /**
   * Check if a command has any registered handlers
   * Used by keyboard handler to decide whether to preventDefault
   */
  hasHandlers(commandId: CommandId): boolean {
    return (this.handlers.get(commandId)?.size ?? 0) > 0;
  }

  /**
   * Get count of handlers for a command (for debugging)
   */
  getHandlerCount(commandId: CommandId): number {
    return this.handlers.get(commandId)?.size ?? 0;
  }

  /**
   * Get all registered command IDs (for debugging)
   */
  getRegisteredCommands(): CommandId[] {
    return Array.from(this.handlers.keys()).filter(id => this.handlers.get(id)!.size > 0);
  }
}

/**
 * React Context for CommandBus
 */
const CommandBusContext = createContext<CommandBus | null>(null);

/**
 * Provider component for CommandBus
 * Wrap your app with this to enable command system
 */
export function CommandBusProvider({ children }: { children: React.ReactNode }) {
  const bus = useMemo(() => new CommandBus(), []);

  return (
    <CommandBusContext.Provider value={bus}>
      {children}
    </CommandBusContext.Provider>
  );
}

/**
 * Hook to access the CommandBus
 * Used by useCommandHandler and KeyboardCommandHandler
 */
export function useCommandBus(): CommandBus {
  const bus = useContext(CommandBusContext);

  if (!bus) {
    throw new Error('useCommandBus must be used within CommandBusProvider');
  }

  return bus;
}
